import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { toast } from 'sonner';
import api from '../../utils/api';
import { 
  ArrowLeft, QrCode, Download, Users, Clock, MapPin, 
  CheckCircle2, XCircle, AlertTriangle, RefreshCw, User, Phone, Mail,
  Sparkles, Target, Star, Briefcase, Send, Rocket, MessageSquare
} from 'lucide-react';
import { formatTime12h, formatTimeRange, formatDateTime12h, formatDateMMDDYYYY } from '../../utils/timeFormat';
import GigBoostModal from '../../components/GigBoostModal';
import InviteTalentModal from '../../components/InviteTalentModal';
import { AIAvatarBadge } from '../../components/AIAvatarBadge';

const JobOverview = () => {
  const { shiftId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrLoading, setQrLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  // Iter-310b — Sibling-role picker. When this shift is part of a
  // multi-role event group, list every other role under the same
  // event so the client can jump between them without bouncing to
  // the dashboard. Per-role applications / invitations / bookings
  // stay bound to that role's own shift_id — switching role just
  // re-routes the page; nothing detaches.
  const [siblingRoles, setSiblingRoles] = useState([]);
  const [eventGroupMeta, setEventGroupMeta] = useState(null);

  // Privacy: clients reach talent only through iEvntür — never raw contact
  // info. Open (or create) the in-app message thread for this talent.
  const openMessageThread = async (talentId) => {
    if (!talentId) return;
    try {
      const res = await api.post('/messages/threads/from-event', {
        shift_id: shiftId,
        talent_id: talentId,
      });
      const threadId = res.data?.thread_id;
      if (!threadId) throw new Error('No thread returned');
      navigate(`/client/messages/${threadId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to open conversation');
    }
  };

  // Secure number-masked calling isn't live yet — never expose a raw number.
  const startSecureCall = () => {
    toast.info('Secure in-app calling is coming soon. Send a message to coordinate with your talent.');
  };


  const fetchOverview = useCallback(async () => {
    try {
      const response = await api.get(`/attendance/overview/${shiftId}`);
      setOverview(response.data);
      // Iter-310b — fetch sibling roles if this shift is part of a
      // multi-role event group. Lightweight call to /client/shifts —
      // we filter client-side so we don't need a new endpoint.
      const eg = response.data?.shift?.event_group_id;
      if (eg) {
        try {
          const all = await api.get('/client/shifts?per_page=200');
          const siblings = (all.data?.shifts || []).filter(
            (s) => s.event_group_id === eg,
          );
          setSiblingRoles(siblings);
          setEventGroupMeta({
            event_group_id: eg,
            event_title: response.data.shift.event_title || response.data.shift.venue_name,
            roles_count: siblings.length,
          });
        } catch {
          // Non-fatal — page still works for the focused role.
        }
      } else {
        setSiblingRoles([]);
        setEventGroupMeta(null);
      }
    } catch (error) {
      toast.error('Failed to load event overview');
      console.error(error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [shiftId]);

  const fetchRecommendations = useCallback(async () => {
    setLoadingRecs(true);
    try {
      const response = await api.get(`/recommendations/gig/${shiftId}?limit=6`);
      const recs = response.data.recommendations || [];
      setRecommendations(recs);
      // Iter-245 — log search impressions so Boost Analytics actually
      // counts when a talent's card shows up on the recommended list.
      if (recs.length > 0) {
        const ids = recs.map(r => r.talent_id).filter(Boolean);
        if (ids.length > 0) {
          api.post('/metrics/search-impressions', { talent_ids: ids, talent_type: 'human' }).catch(() => {});
        }
      }
    } catch (error) {
      console.error('Failed to load recommendations:', error);
    } finally {
      setLoadingRecs(false);
    }
  }, [shiftId]);

  useEffect(() => {
    fetchOverview();
    fetchRecommendations();
    // Auto-refresh every 30 seconds for real-time updates
    const interval = setInterval(fetchOverview, 30000);
    return () => clearInterval(interval);
  }, [fetchOverview, fetchRecommendations]);

  const generateQRCode = async () => {
    setQrLoading(true);
    try {
      await api.post(`/attendance/qr/generate/${shiftId}`);
      await fetchOverview();
      toast.success('QR code generated');
    } catch (error) {
      toast.error('Failed to generate QR code');
    } finally {
      setQrLoading(false);
    }
  };

  const downloadPDF = async () => {
    setPdfLoading(true);
    try {
      const response = await api.get(`/attendance/pdf/${shiftId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `signin_sheet_${shiftId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Sign-in sheet downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchOverview();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      checked_in: { label: 'Checked In', variant: 'default', icon: CheckCircle2, color: 'bg-green-500' },
      checked_out: { label: 'Checked Out', variant: 'secondary', icon: CheckCircle2, color: 'bg-blue-500' },
      not_checked_in: { label: 'Not Checked In', variant: 'outline', icon: Clock, color: 'bg-gray-400' },
      late: { label: 'Late', variant: 'destructive', icon: AlertTriangle, color: 'bg-amber-500' },
      no_show: { label: 'No Show', variant: 'destructive', icon: XCircle, color: 'bg-red-500' }
    };
    const config = statusConfig[status] || statusConfig.not_checked_in;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-4xl mx-auto text-center py-12">
          <h2 className="text-xl font-semibold text-slate-700">Event not found</h2>
          <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
          </Button>
        </div>
      </div>
    );
  }

  const { shift, staff, attendance_summary, qr_code_token } = overview;
  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="flex-shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-slate-900 truncate">{shift.venue_name}</h1>
              <p className="text-sm text-slate-500 truncate">{formatDateMMDDYYYY(shift.shift_date)} • {formatTimeRange(shift.start_time, shift.end_time)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowInviteModal(true)}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="invite-talent-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Invite Talent
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowBoostModal(true)}
              className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
              data-testid="boost-gig-btn"
            >
              <Rocket className="w-4 h-4 mr-2" />
              Boost Gig
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadPDF}
              disabled={pdfLoading}
            >
              <Download className="w-4 h-4 mr-2" />
              {pdfLoading ? 'Generating...' : 'Sign-In Sheet'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Iter-310b — Multi-role event picker. Lets the client jump
            between the roles attached to this event without leaving
            the overview. Each chip routes to that role's own JobOverview
            URL — so applicants / invitations / bookings stay bound to
            the role's underlying shift_id. */}
        {siblingRoles.length > 1 && eventGroupMeta && (
          <div
            className="rounded-xl border border-cyan-200 bg-gradient-to-r from-cyan-50/70 to-white p-3 sm:p-4"
            data-testid="event-overview-roles-picker"
          >
            <div className="flex items-start sm:items-center justify-between gap-2 flex-wrap mb-3">
              <div>
                <p className="text-[11px] uppercase font-bold tracking-wide text-cyan-700">
                  Roles in this event
                </p>
                <p className="text-sm font-semibold text-slate-900">
                  {eventGroupMeta.event_title}
                  <span className="ml-2 text-xs font-normal text-slate-500">
                    {eventGroupMeta.roles_count} {eventGroupMeta.roles_count === 1 ? 'role' : 'roles'}
                  </span>
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {siblingRoles.map((sib) => {
                const isActive = sib.id === shiftId;
                const filled = sib.bookings_count ?? 0;
                const needed = sib.talent_needed ?? 0;
                const pending = sib.pending_applications ?? 0;
                return (
                  <button
                    key={sib.id}
                    type="button"
                    onClick={() => navigate(`/client/job-overview/${sib.id}`)}
                    className={
                      'flex flex-col items-start gap-0.5 px-3 py-2 rounded-lg border text-left transition-colors ' +
                      (isActive
                        ? 'border-cyan-500 bg-cyan-100/60 text-cyan-900'
                        : 'border-gray-200 bg-white text-slate-700 hover:border-cyan-300 hover:bg-cyan-50/40')
                    }
                    data-testid={`event-overview-role-chip-${sib.id}`}
                  >
                    <span className="text-xs font-semibold flex items-center gap-2">
                      {sib.role_name}
                      {isActive && (
                        <Badge className="bg-cyan-600 text-white text-[10px] px-1.5 py-0">Current</Badge>
                      )}
                    </span>
                    <span className="text-[10px] text-slate-500">
                      {filled}/{needed} filled
                      {pending > 0 && <> · <span className="text-indigo-600 font-medium">{pending} pending</span></>}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Event Details & Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Event Info */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Event Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium">{shift.venue_name}</p>
                  <p className="text-slate-500">{shift.city}, {shift.state}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-400" />
                <span className="text-sm">{formatTimeRange(shift.start_time, shift.end_time)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-400" />
                <span className="text-sm">{shift.role_name}</span>
              </div>
            </CardContent>
          </Card>

          {/* Attendance Stats */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Attendance Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{attendance_summary.checked_in}</p>
                  <p className="text-xs text-green-700">Checked In</p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{attendance_summary.checked_out}</p>
                  <p className="text-xs text-blue-700">Checked Out</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-600">{attendance_summary.not_checked_in}</p>
                  <p className="text-xs text-slate-700">Pending</p>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">{attendance_summary.total}</p>
                  <p className="text-xs text-amber-700">Total Staff</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* QR Code */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                <QrCode className="w-4 h-4" />
                Check-In QR Code
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center">
              {qr_code_token ? (
                <>
                  <img 
                    src={`${apiUrl}/api/attendance/qr/image/${qr_code_token}`}
                    alt="Check-in QR Code"
                    className="w-32 h-32 border rounded-lg"
                  />
                  <p className="text-xs text-slate-500 mt-2">Code: {qr_code_token}</p>
                </>
              ) : (
                <Button 
                  onClick={generateQRCode} 
                  disabled={qrLoading}
                  className="w-full"
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  {qrLoading ? 'Generating...' : 'Generate QR Code'}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Staff Roster */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Staff Roster</CardTitle>
                <CardDescription>
                  {staff.length} staff members assigned to this event
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="all">
              <TabsList className="mb-4 flex-wrap h-auto">
                <TabsTrigger value="all">All ({staff.length})</TabsTrigger>
                <TabsTrigger value="checked_in">
                  Checked In ({staff.filter(s => s.attendance_status === 'checked_in').length})
                </TabsTrigger>
                <TabsTrigger value="checked_out">
                  Checked Out ({staff.filter(s => s.attendance_status === 'checked_out').length})
                </TabsTrigger>
                <TabsTrigger value="pending">
                  Pending ({staff.filter(s => s.attendance_status === 'not_checked_in').length})
                </TabsTrigger>
              </TabsList>

              {['all', 'checked_in', 'checked_out', 'pending'].map(tab => (
                <TabsContent key={tab} value={tab} className="space-y-3">
                  {staff
                    .filter(s => {
                      if (tab === 'all') return true;
                      if (tab === 'pending') return s.attendance_status === 'not_checked_in';
                      return s.attendance_status === tab;
                    })
                    .map((member) => (
                      <div 
                        key={member.booking_id}
                        className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4 bg-white border rounded-lg hover:shadow-sm transition-shadow"
                        data-testid={`staff-member-${member.user_id}`}
                      >
                        {/* Profile Photo */}
                        <div className="flex-shrink-0">
                          {member.profile_photo_url ? (
                            <img 
                              src={member.profile_photo_url} 
                              alt={`${member.first_name} ${member.last_name}`}
                              className="w-14 h-14 rounded-full object-cover border-2 border-slate-100"
                            />
                          ) : (
                            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center">
                              <User className="w-6 h-6 text-slate-400" />
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-grow min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-slate-900 truncate">
                              {member.first_name} {member.last_name}
                            </h4>
                            {getStatusBadge(member.attendance_status)}
                          </div>
                          <p className="text-sm text-slate-500 truncate">{member.role_name}</p>
                          <div className="flex items-center flex-wrap gap-2 mt-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 px-3 text-xs border-cyan-200 text-cyan-700 hover:bg-cyan-50"
                              onClick={() => openMessageThread(member.user_id)}
                              data-testid={`message-talent-${member.user_id}`}
                            >
                              <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Message
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 px-3 text-xs border-slate-200 text-slate-600 hover:bg-slate-50"
                              onClick={startSecureCall}
                              data-testid={`call-talent-${member.user_id}`}
                            >
                              <Phone className="w-3.5 h-3.5 mr-1.5" /> Call
                            </Button>
                          </div>
                        </div>

                        {/* Times */}
                        <div className="flex-shrink-0 sm:text-right text-sm">
                          {member.checkin_time && (
                            <div>
                              <span className="text-slate-400">In:</span>{' '}
                              <span className="font-medium text-green-600">
                                {formatDateTime12h(member.checkin_time)}
                              </span>
                            </div>
                          )}
                          {member.checkout_time && (
                            <div>
                              <span className="text-slate-400">Out:</span>{' '}
                              <span className="font-medium text-blue-600">
                                {formatDateTime12h(member.checkout_time)}
                              </span>
                            </div>
                          )}
                          {member.checkin_distance_meters && (
                            <div className="text-xs text-slate-400 mt-1">
                              {member.checkin_distance_meters.toFixed(0)}m from venue
                            </div>
                          )}
                        </div>

                        {/* Verification Indicators */}
                        <div className="flex-shrink-0 flex items-center gap-2">
                          {member.checkin_selfie_url && (
                            <div className="w-8 h-8 rounded border overflow-hidden" title="Selfie verified">
                              <img src={member.checkin_selfie_url} alt="Selfie" className="w-full h-full object-cover" />
                            </div>
                          )}
                          {member.digital_signature_url && (
                            <Badge variant="outline" className="text-xs">Signed</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  
                  {staff.filter(s => {
                    if (tab === 'all') return true;
                    if (tab === 'pending') return s.attendance_status === 'not_checked_in';
                    return s.attendance_status === tab;
                  }).length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      No staff members in this category
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>

        {/* Recommended Talent for This Gig */}
        {recommendations.length > 0 && (
          <Card className="bg-gradient-to-r from-[#03DFFC]/5 to-[#FF2D95]/5 border-[#03DFFC]/20">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#03DFFC]" />
                  <CardTitle className="text-lg text-slate-900">Recommended Talent for This Gig</CardTitle>
                </div>
                <Link to={`/client/discover?gig=${shiftId}`}>
                  <Button variant="outline" size="sm">View All Matches</Button>
                </Link>
              </div>
              <CardDescription>Best matches based on skills, location, and past performance</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {recommendations.map(rec => (
                  <div 
                    key={rec.talent_id}
                    onClick={() => navigate(`/client/discover/${rec.talent_id}?source=ai_match`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/client/discover/${rec.talent_id}?source=ai_match`);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    className="flex items-center gap-3 p-3 bg-white rounded-lg border shadow-sm cursor-pointer hover:shadow-md hover:border-cyan-300 transition-all focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    data-testid={`recommendation-${rec.talent_id}`}
                    title={`View ${rec.name}'s profile`}
                  >
                    {rec.profile_photo_url ? (
                      <div className="relative">
                        <img src={rec.profile_photo_url} alt={rec.name} className="w-12 h-12 rounded-full object-cover" />
                        {rec.is_ai_avatar && (
                          <span className="absolute -bottom-1 -right-1"><AIAvatarBadge variant="dot" /></span>
                        )}
                      </div>
                    ) : (
                      <div className="relative">
                        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-slate-400" />
                        </div>
                        {rec.is_ai_avatar && (
                          <span className="absolute -bottom-1 -right-1"><AIAvatarBadge variant="dot" /></span>
                        )}
                      </div>
                    )}
                    <div className="flex-grow min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium text-slate-900 truncate hover:text-cyan-600">{rec.name}</h4>
                        {rec.is_ai_avatar && <AIAvatarBadge />}
                        <Badge 
                          className={`text-[10px] px-1.5 py-0 ${
                            rec.match_label === 'Top Match' 
                              ? 'bg-green-100 text-green-700 border-green-300' 
                              : rec.match_label === 'Recommended'
                                ? 'bg-blue-100 text-blue-700 border-blue-300'
                                : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {rec.match_label === 'Top Match' && <Target className="w-2.5 h-2.5 mr-0.5 inline" />}
                          {rec.match_label}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                        {rec.average_rating && (
                          <span className="flex items-center">
                            <Star className="w-3 h-3 text-yellow-500 fill-yellow-500 mr-0.5" />
                            {rec.average_rating.toFixed(1)}
                          </span>
                        )}
                        {rec.distance_miles && (
                          <span className="flex items-center">
                            <MapPin className="w-3 h-3 mr-0.5" />
                            {rec.distance_miles} mi
                          </span>
                        )}
                        {rec.is_favorite && (
                          <Badge className="bg-pink-100 text-pink-600 text-[10px] px-1 py-0">Favorite</Badge>
                        )}
                      </div>
                      {rec.recommendation_reasons && rec.recommendation_reasons.length > 0 && (
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {rec.recommendation_reasons.slice(0, 2).map((reason, i) => (
                            <span key={i} className="text-[9px] text-slate-400 bg-slate-50 px-1.5 rounded">
                              {reason}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await api.post('/bookings/direct-book/' + shiftId, {
                              talent_ids: [rec.talent_id],
                              booking_type: 'confirmed'
                            });
                            toast.success(`${rec.name} booked!`);
                            fetchOverview();
                            fetchRecommendations();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Failed to book');
                          }
                        }}
                        className="h-7 px-2 text-green-600 hover:bg-green-50"
                        title="Direct Book"
                        data-testid={`recommendation-direct-book-${rec.talent_id}`}
                      >
                        <Briefcase className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Iter-300 — Client invite/broadcast for their own gig. Uses
          the same modal as admin, pointed at /client wrappers. */}
      <InviteTalentModal
        open={showInviteModal}
        onOpenChange={setShowInviteModal}
        shiftId={shiftId}
        shiftMeta={shift ? {
          city: shift.city, state: shift.state,
          venue_name: shift.venue_name,
          shift_date: shift.shift_date,
        } : null}
        apiBase="/client"
      />

      {/* Gig Boost Modal */}
      <GigBoostModal
        open={showBoostModal}
        onClose={() => setShowBoostModal(false)}
        shiftId={shiftId}
        shiftName={shift?.venue_name}
        onBoostSuccess={() => {
          fetchOverview();
          toast.success('Gig boosted successfully!');
        }}
      />
    </div>
  );
};

export default JobOverview;
